/**
 * Formato canônico do verso no Wallet (Apple backFields / Google textModules):
 * texto + `\n` + `<a href>`. Sem `<p>`, `<br>`, listas.
 */

const ANCHOR_OPEN = '\u0000A';
const ANCHOR_CLOSE = '\u0000/A\u0000';

function decodeEntities(s: string): string {
  return s
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;|&apos;/gi, "'")
    .replace(/&#x([0-9a-f]+);/gi, (_, h: string) => String.fromCharCode(parseInt(h, 16)))
    .replace(/&#(\d+);/g, (_, n: string) => String.fromCharCode(Number(n)));
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function extractHref(attrs: string): string {
  const quoted = attrs.match(/href\s*=\s*(["'])([\s\S]*?)\1/i);
  if (quoted?.[2] != null) return quoted[2];
  const bare = attrs.match(/href\s*=\s*([^\s>]+)/i);
  return bare?.[1] ?? '';
}

export function sanitizeHref(href: string): string {
  const t = href.trim();
  if (!t) return '';
  if (/^(javascript|data|vbscript):/i.test(t)) return '';
  if (/^https?:\/\//i.test(t) || /^mailto:/i.test(t)) return t;
  if (t.startsWith('//')) return `https:${t}`;
  if (/^www\./i.test(t)) return `https://${t}`;
  return '';
}

function restoreAnchors(s: string): string {
  return s
    .replace(new RegExp(`${ANCHOR_OPEN}([^\\u0000]*)\\u0000`, 'g'), (_, href: string) => {
      const safe = sanitizeHref(href);
      return safe ? `<a href="${safe}">` : '';
    })
    .replaceAll(ANCHOR_CLOSE, '</a>');
}

function mapAnchorSegments(s: string, onText: (text: string) => string): string {
  const re = /<a href="([^"]*)">([\s\S]*?)<\/a>/gi;
  let out = '';
  let last = 0;
  let match: RegExpExecArray | null;
  while ((match = re.exec(s))) {
    out += onText(s.slice(last, match.index));
    const href = sanitizeHref(match[1] ?? '');
    const inner = onText(match[2] ?? '');
    out += href ? `<a href="${escapeHtml(href)}">${inner}</a>` : inner;
    last = match.index + match[0].length;
  }
  out += onText(s.slice(last));
  return out;
}

/** TipTap / HTML legado → valor persistido no passbook. */
export function htmlToWalletValue(html: string): string {
  if (!html || !html.trim()) return '';

  let s = html.trim();

  s = s.replace(/<a\b([^>]*)>([\s\S]*?)<\/a>/gi, (_full, attrs: string, inner: string) => {
    const href = sanitizeHref(decodeEntities(extractHref(attrs)));
    return href ? `${ANCHOR_OPEN}${href}\u0000${inner}${ANCHOR_CLOSE}` : inner;
  });
  s = s.replace(/<\/a>/gi, '');

  s = s.replace(/<br\s*\/?>/gi, '\n');
  s = s.replace(/<li[^>]*>/gi, '• ');
  s = s.replace(/<\/li>/gi, '\n');
  s = s.replace(/<\/?(ul|ol)[^>]*>/gi, '');
  s = s.replace(/<\/p>/gi, '\n');
  s = s.replace(/<p[^>]*>/gi, '');
  s = s.replace(/<\/h[1-6]>/gi, '\n');
  s = s.replace(/<h[1-6][^>]*>/gi, '');
  s = s.replace(/<\/div>/gi, '\n');
  s = s.replace(/<div[^>]*>/gi, '');
  s = s.replace(/<\/tr>/gi, '\n');
  s = s.replace(/<[^>]+>/g, '');

  s = decodeEntities(s);
  s = restoreAnchors(s);

  s = s.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  s = s
    .split('\n')
    .map((line) => line.trim())
    .join('\n');
  s = s.replace(/\n{2,}(?=• )/g, '\n');
  s = s.replace(/\n{3,}/g, '\n\n');
  s = s.replace(/^\n+|\n+$/g, '');
  return s;
}

/** Valor Wallet → HTML que o TipTap carrega (um `<p>` com `<br>`). */
export function walletValueToEditorHtml(value: string): string {
  const wallet = htmlToWalletValue(value);
  if (!wallet) return '';
  const inner = wallet
    .split('\n')
    .map((line) => mapAnchorSegments(line, escapeHtml))
    .join('<br>');
  return `<p>${inner}</p>`;
}

/** Valor Wallet → HTML do preview (quebra visível, `<a>` clicável). */
export function walletValueToPreviewHtml(value: string): string {
  const wallet = htmlToWalletValue(value);
  if (!wallet) return '';
  return wallet
    .split('\n')
    .map((line) => mapAnchorSegments(line, escapeHtml))
    .join('<br>');
}
